import { normalizeWidgetTheme, APIURL, appendFlatpickrBaseStyles, cancelSelectEvent, getCSSStyles, getWidgetDataURI, apiGet, toggleWidgetTheme, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML } from './../../helpers';
import { SearchInsiderConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

interface InsiderSearchTransaction {
    symbol: string;
    filing_date: string;
    transaction_date: string;
    transaction_amount: number;
    acquisition_or_disposition: string;
    securities_transacted: number;
    price: number;
    reporting_name: string;
    transaction_type: string;
    type_of_owner: string;
    form_type: string;
}

interface InsiderSearchApiData {
    parameters: {
        insider_name: string;
        days_back: number;
        min_transaction_value: number;
        cutoff_date: string;
        search_date: string;
    };
    search_summary: {
        total_records: number;
        matched_names_count: number;
        symbols_count: number;
        total_value: number;
        acquisition_count: number;
        disposition_count: number;
        earliest_date: string | null;
        latest_date: string | null;
    };
    matched_names: string[];
    transactions: InsiderSearchTransaction[];
}

export class TraderverseSearchInsider {
    private container: HTMLElement;
    private config: SearchInsiderConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private updateInterval: number | null = null;
    private shadowRoot: ShadowRoot;
    private currentData: InsiderSearchApiData | null = null;
    private widgetResizeObserver: ResizeObserver | null = null;
    private filters = {
        searchTerm: 'b' as string,
        daysBack: 90 as number | '',
        minTransactionValue: 500000 as number | ''
    };

    constructor(config: SearchInsiderConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.applyConfigFilters();
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<SearchInsiderConfig> {
        return {
            headerPinned: true,
            searchTerm: 'b',
            daysBack: 90,
            minTransactionValue: 500000,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true
        };
    }

    private applyConfigFilters(): void {
        if (typeof this.config.searchTerm === 'string') this.filters.searchTerm = this.config.searchTerm;
        if (typeof this.config.daysBack === 'number') this.filters.daysBack = this.config.daysBack;
        if (typeof this.config.minTransactionValue === 'number') this.filters.minTransactionValue = this.config.minTransactionValue;
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        this.attachStyles();
        if (this.filters.searchTerm.trim()) {
            await this.fetchData();
        } else {
            this.updateDisplay();
        }
        if (widget) widget.classList.remove('loading');

        this.emit('ready', {});
        this.config.onReady?.();
        cancelSelectEvent(this.shadowRoot);
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == "number" ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title stock">${this.getTitleIconSvg()}Search Insider</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
            <div class="item">
              <button class="filters-toggle" type="button">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
            <i class="pi pi-ellipsis-v"></i>
          </div>
        </div>

        <div class="inBody">
          <div class="optionsRow">
            <div class="item">
              <input type="text" class="filter-search-term" placeholder="Search insider name..." value="${this.filters.searchTerm}" aria-label="Search insider name" />
            </div>
            <span></span>
            <div class="filtersBox sideBar hidden">
              <div class="item">
                <label>Days Back</label>
                <input type="number" class="filter-days-back" min="1" step="1" placeholder="e.g. 90" value="${this.filters.daysBack}" />
              </div>
              <div class="item">
                <label>Min Transaction Value</label>
                <input type="number" class="filter-min-transaction-value" min="0" step="1" placeholder="e.g. 500000" value="${this.filters.minTransactionValue}" />
              </div>
            </div>
          </div>
          <div class="chartDv" id="eventsContainer"></div>
        </div>
      </div>
    `;

        this.shadowRoot.innerHTML = widgetHtml;
        setupHeaderPin(this.shadowRoot, this.config);
        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        const filtersToggle = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
        const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
        if (filtersToggle && filtersBox) {
            filtersToggle.addEventListener('click', (e) => {
                e.stopPropagation();
                filtersBox.classList.toggle('hidden');
            });
        }

        const searchInput = this.shadowRoot.querySelector('.filter-search-term') as HTMLInputElement | null;
        const runSearch = () => {
            this.filters.searchTerm = searchInput?.value.trim() ?? '';
            this.config.searchTerm = this.filters.searchTerm;
            this.fetchData();
        };

        searchInput?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') runSearch();
        });
        searchInput?.addEventListener('change', runSearch);

        const updateNumericFiltersAndFetch = () => {
            this.filters.daysBack = this.readNumberInput('.filter-days-back');
            this.filters.minTransactionValue = this.readNumberInput('.filter-min-transaction-value');
            if (this.filters.daysBack !== '') this.config.daysBack = this.filters.daysBack;
            if (this.filters.minTransactionValue !== '') this.config.minTransactionValue = this.filters.minTransactionValue;
            if (this.filters.searchTerm.trim()) this.fetchData();
        };

        [
            '.filter-days-back',
            '.filter-min-transaction-value'
        ].forEach((selector) => {
            const input = this.shadowRoot.querySelector(selector) as HTMLInputElement | null;
            input?.addEventListener('change', updateNumericFiltersAndFetch);
        });
    }

    private readNumberInput(selector: string): number | '' {
        const input = this.shadowRoot.querySelector(selector) as HTMLInputElement | null;
        if (!input || input.value.trim() === '') return '';
        const value = parseFloat(input.value);
        return Number.isNaN(value) ? '' : value;
    }

    private buildQueryParams(): Record<string, string | number> {
        const query: Record<string, string | number> = {
            searchTerm: this.filters.searchTerm.trim()
        };
        if (this.filters.daysBack !== '') query.daysBack = this.filters.daysBack;
        if (this.filters.minTransactionValue !== '') query.minTransactionValue = this.filters.minTransactionValue;
        return query;
    }

    private async attachStyles(): Promise<void> {
        try {
            appendFlatpickrBaseStyles(this.shadowRoot);
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async fetchData(): Promise<void> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');

        if (!this.filters.searchTerm.trim()) {
            this.currentData = null;
            this.updateDisplay();
            if (widget) widget.classList.remove('loading');
            return;
        }

        try {
            const response = await apiGet({
                url: `${APIURL}/v1/equity-market/insider-search`,
                query: this.buildQueryParams()
            });

            if (!response.ok) throw new Error('Failed to fetch insider search data');
            const json = await response.json();
            this.currentData = (json?.data as InsiderSearchApiData | undefined) ?? null;

            this.updateDisplay();
            this.emit('dataUpdate', this.currentData);
        } catch (err) {
            console.error('Error fetching insider search data:', err);
            this.currentData = null;
            this.updateDisplay();
        } finally {
            if (widget) widget.classList.remove('loading');
        }
    }

    private renderTransactions(data: InsiderSearchApiData | null): void {
        const container = this.shadowRoot.querySelector('#eventsContainer');
        const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement;

        if (!container || !chartDv) return;

        if (!this.filters.searchTerm.trim()) {
            chartDv.removeAttribute('data-testing-id');
            container.innerHTML = `<p style="text-align:center;">Enter an insider name to search</p>`;
            return;
        }

        const transactions = data?.transactions ?? [];
        const summary = data?.search_summary;
        const hasData = transactions.length > 0;

        if (hasData) {
            chartDv.setAttribute('data-testing-id', 'Search Insider');
        } else {
            chartDv.removeAttribute('data-testing-id');
        }

        if (!hasData) {
            container.innerHTML = getNoDataHTML();
            return;
        }

        const formatNumber = (val: any, maxDigits: number = 0): string => {
            if (val === null || val === undefined) return '-';
            const num = typeof val === 'number' ? val : Number(val);
            if (Number.isNaN(num)) return String(val);
            return num.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: maxDigits
            });
        };

        const formatCurrency = (val: any): string => {
            if (val === null || val === undefined) return '-';
            const num = typeof val === 'number' ? val : Number(val);
            if (Number.isNaN(num)) return String(val);
            return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        };

        const formatDisposition = (val: string): string => {
            if (val === 'A') return 'Acquisition';
            if (val === 'D') return 'Disposition';
            return val || '-';
        };

        let html = '';

        if (summary) {
            html += `
        <div class="insider-search-summary" style="margin-bottom: 12px; font-size: 13px; opacity: 0.85;">
          <span>${formatNumber(summary.total_records)} transactions</span>
          <span style="margin: 0 8px;">|</span>
          <span>${formatNumber(summary.matched_names_count)} matched names</span>
          <span style="margin: 0 8px;">|</span>
          <span>${formatNumber(summary.symbols_count)} symbols</span>
          <span style="margin: 0 8px;">|</span>
          <span>${formatCurrency(summary.total_value)} total value</span>
        </div>`;
        }

        html += `
      <div class="eventsMain customGrid shareStructure--" style="--grid-template-columns: minmax(70px,0.7fr) minmax(180px,1.6fr) minmax(90px,0.85fr) minmax(90px,0.85fr) minmax(110px,1fr) minmax(110px,1fr) minmax(110px,1fr) minmax(90px,0.85fr) minmax(120px,1.1fr) minmax(180px,1.8fr) minmax(70px,0.7fr); --grid-column-gap: 14px; min-width: 1400px;">
        <div class="item header">
          <p>Symbol</p>
          <p>Reporting Name</p>
          <p>Form</p>
          <p>Type</p>
          <p>Transaction Date</p>
          <p>Filing Date</p>
          <p>Securities</p>
          <p>Price</p>
          <p>Amount</p>
          <p>Owner Type</p>
          <p>A/D</p>
        </div>
    `;

        for (const row of transactions) {
            const logoUrl = `https://storage.googleapis.com/analytics-images-traderverse/stocks_v1/${row.symbol}.svg`;
            const isDisposition = row.acquisition_or_disposition === 'D';
            html += `
        <div class="item body">
          <p><img src="${logoUrl}" alt="${row.symbol}" style="width:24px;height:24px;border-radius:4px;" /> ${row.symbol ?? '-'}</p>
          <p title="${row.reporting_name ?? '-'}">${row.reporting_name ?? '-'}</p>
          <p>${row.form_type ?? '-'}</p>
          <p>${row.transaction_type ?? '-'}</p>
          <p>${row.transaction_date ?? '-'}</p>
          <p>${row.filing_date ?? '-'}</p>
          <p>${formatNumber(row.securities_transacted)}</p>
          <p>${formatCurrency(row.price)}</p>
          <p class="stat left ${isDisposition ? 'down' : 'up'}"><span>${formatCurrency(row.transaction_amount)}</span></p>
          <p title="${row.type_of_owner ?? '-'}">${row.type_of_owner ?? '-'}</p>
          <p class="stat ${isDisposition ? 'down' : 'up'}"><span>${formatDisposition(row.acquisition_or_disposition)}</span></p>
        </div>`;
        }

        html += `</div>`;
        container.innerHTML = html;
    }

    private updateDisplay(): void {
        this.renderTransactions(this.currentData);
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }

    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }

    public setSearchTerm(searchTerm: string): void {
        this.filters.searchTerm = searchTerm;
        this.config.searchTerm = searchTerm;
        const input = this.shadowRoot.querySelector('.filter-search-term') as HTMLInputElement | null;
        if (input) input.value = searchTerm;
        this.fetchData();
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
        if (!this.shadowRoot) return;

        this.config.theme = toggleWidgetTheme(
            this.shadowRoot,
            theme
        ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

        this.updateDisplay();
    }

    public destroy(): void {
        if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }

    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseSearchInsider: typeof TraderverseSearchInsider; } }
if (typeof window !== 'undefined') { window.TraderverseSearchInsider = TraderverseSearchInsider; }
